/**
 * Meaningful-change detection for the project autosave.
 *
 * Operator ask (Peter, 2026-08-16): *"it shouldn't keep auto saving. however,
 * it should actually check if any meaningful updates were made before
 * suggesting user to save."*
 *
 * The old dirty bit was a change DETECTOR — a `useEffect` watching 13 state
 * containers that set `hasUnsavedChanges = true` whenever any of them changed
 * IDENTITY. Hydrating a project necessarily hands React fresh arrays and
 * objects, so simply opening a quote to look at it marked it dirty, and the 2s
 * autosave then wrote a full envelope back, bumped `updated_at`, and produced
 * `PROJECT_STALE_WRITE` 409s on a second device.
 *
 * This module is the COMPARATOR that replaces it: snapshot a normalised
 * fingerprint of the persisted state at load and after each successful save,
 * then compare against it before claiming the operator changed anything.
 *
 * ── The safety property that shapes every decision below ──────────────────
 *
 * A false POSITIVE costs a redundant save. A false NEGATIVE loses the
 * operator's work silently. So this must never miss a real change, which rules
 * out sampling, truncation, or hashing a prefix: every semantic byte is read.
 * The normaliser therefore works by an explicit VOLATILE denylist (things that
 * provably differ between two sessions holding identical data) and passes
 * everything else through verbatim. Adding a new persisted field to the save
 * envelope needs no change here — unknown fields are included by default,
 * which fails safe toward "saved" rather than "lost".
 *
 * Cost is one `JSON.stringify` of the projected state per fire (single-digit
 * ms on a photo-heavy job). We keep the resulting string rather than hashing
 * it: a string compare is free and has no collision risk.
 */

/**
 * Values that legitimately differ between two sessions holding identical
 * persisted data, and therefore must not count as an edit.
 *
 * - `blob:` / object URLs are minted per session by `URL.createObjectURL`.
 * - `data:` URLs are rehydrated from Dexie blobs on load and can be re-encoded.
 * - `File` / `Blob` instances are live handles, never persisted as-is (the save
 *   envelope strips `videos[].file` and `videos[].url` for exactly this reason).
 */
function normaliseValue(value: unknown): unknown {
    if (typeof value === 'string') {
        // Collapse to a constant so a re-minted URL for the SAME underlying
        // object does not read as an edit. Length is retained for data: URLs so
        // a genuinely different payload still differs.
        if (value.startsWith('blob:')) return '[blob-url]';
        if (value.startsWith('data:')) return `[data-url:${value.length}]`;
        return value;
    }
    if (typeof value === 'function' || typeof value === 'undefined') return undefined;
    if (typeof File !== 'undefined' && value instanceof File) return '[file]';
    if (typeof Blob !== 'undefined' && value instanceof Blob) return `[blob:${value.size}]`;
    return value;
}

/**
 * `JSON.stringify` with deterministic key order.
 *
 * Object key order is insertion order in JS, and the load path builds objects
 * in a different order than the edit path does (a spread, a `normalizeToSnake`
 * pass, and a server round-trip all reorder keys). Sorting removes that as a
 * source of phantom edits.
 */
/**
 * Derived pricing detail, dropped at ANY depth.
 *
 * `scope[].costBreakdown` is recomputed from the rate book every time a project
 * loads, so a live open re-emits it with the same numbers in a different shape
 * (measured 2026-08-16: `{rate:120,hours:8,total:960,trade:"Building
 * Supervisor"}` came back as `{trade:"Building Supervisor",hours:8,rate:120,
 * workers:1,total:960,...}`). That made `scope` — the single most important
 * container — differ on every open, which is the last thing keeping the
 * open-a-project autosave alive.
 *
 * Excluding it cannot hide an operator edit: the operator edits an item's
 * amount/title/description/inclusions, never the internal breakdown, and any
 * change that moves the money moves `amount` too, which IS fingerprinted.
 */
const DERIVED_KEYS = new Set(['costBreakdown', 'cost_breakdown']);

function stableStringify(input: unknown): string {
    // Track the active recursion path, not every object visited. A saved quote
    // may expose the same canonical scope array at both `scope` and
    // `quoteData.scope`; that shared alias is not a cycle and must fingerprint
    // identically to two equal arrays reconstructed by the API.
    const ancestors = new WeakSet<object>();

    const walk = (value: unknown): unknown => {
        const normalised = normaliseValue(value);
        if (normalised === null || typeof normalised !== 'object') return normalised;

        // A cycle cannot be persisted, so its exact shape does not matter —
        // only that the walk terminates.
        if (ancestors.has(normalised as object)) return '[circular]';
        ancestors.add(normalised as object);
        try {
            if (Array.isArray(normalised)) return normalised.map(walk);

            const out: Record<string, unknown> = {};
            for (const key of Object.keys(normalised as Record<string, unknown>).sort()) {
                if (DERIVED_KEYS.has(key)) continue;
                const child = walk((normalised as Record<string, unknown>)[key]);
                // Drop undefined so `{a: undefined}` and `{}` compare equal, matching
                // what JSON.stringify persists.
                if (child !== undefined) out[key] = child;
            }
            return out;
        } finally {
            ancestors.delete(normalised as object);
        }
    };

    try {
        return JSON.stringify(walk(input)) ?? '';
    } catch (err) {
        // Never let fingerprinting break the save path. An unfingerprintable
        // state falls back to a value that can never match a previous
        // fingerprint, so the comparator degrades to today's always-dirty
        // behaviour rather than swallowing an edit.
        console.warn('[QM] project fingerprint failed; treating state as changed:', (err as Error)?.message || err);
        return `[unfingerprintable:${Math.random()}]`;
    }
}

/**
 * The persisted, operator-editable surface of a project.
 *
 * Mirrors the `projectRecord` envelope built in
 * `use-project-persistence.ts` `saveCurrentProject`. Deliberately EXCLUDED:
 *
 * - `id`, `created_at`, `updated_at`, `expectedUpdatedAt` — server/lifecycle
 *   metadata. `updated_at` is stamped `new Date()` on every save, so including
 *   it would make every project permanently dirty.
 * - `quoteStatus`, `quoteStatusChangedAt`, `projectType`, `qmQuoteNo` — owned
 *   by other surfaces (AllQuotes, the importer) and merely carried through the
 *   envelope so a save does not drop them. An operator cannot edit them here.
 * - `projectCostingSheet` — DERIVED from scope + the pricing book, and
 *   REGENERATED on project open: a live open observed it bump `v1.11` -> `v1.12`
 *   with a fresh `generated_at`, which made every open of every project look
 *   like an edit and produced exactly the autosave Peter reported. Excluding it
 *   cannot lose an operator edit, because it holds none: when its INPUTS change
 *   the inputs are fingerprinted, mark the project dirty, and the regenerated
 *   sheet rides along in that same save envelope. The converse — treating a
 *   pricing-book edit as a change to all 1,500 projects — is the behaviour we
 *   actively do not want.
 * - `completedStages`, `quoteNumber`, `aiContext` — DERIVED at save time from
 *   fields that are already fingerprinted (`getCompletedStagesForSave` reads
 *   photos/scope/quoteData; `quoteNumber` reads `formData.orderNumber`;
 *   aiContext reads scope/quoteData plus timestamps). Including them would add
 *   timestamp churn without adding coverage.
 * - `pipelineVersion` — a constant.
 *
 * `pricingMatchesDismissed` is accepted by the interface but the App-level
 * caller does not hold it: the save path reads it straight from Dexie rather
 * than from React state, so it cannot change in a way this effect would ever
 * observe. It stays in the interface so the field is covered the day it does
 * become App state.
 *
 * The containers the OLD detector's dep array omitted are covered below
 * (`quoteTitle`, `projectWMS`, `projectStrategy`, `scopeGstInclusive`,
 * `projectDefaultMargin`, `appliedLearnings`, plus `completedStages` via its
 * inputs) — editing only those used to leave the dirty bit false, which was the
 * opposite failure and equally real. `projectCostingSheet` is the one exception,
 * excluded for the derived-artifact reason given above.
 */
export interface ProjectFingerprintInput {
    quoteTitle?: unknown;
    titleEditedByUser?: unknown;
    formData?: unknown;
    stage?: unknown;
    projectWorkflowMode?: unknown;
    legacyBannerDismissed?: unknown;
    photos?: unknown;
    documents?: unknown;
    videos?: unknown;
    videoTranscripts?: unknown;
    selectedHeroImage?: unknown;
    projectSummary?: unknown;
    analysis?: unknown;
    photoAnalysis?: unknown;
    scopeGstInclusive?: unknown;
    scope?: unknown;
    quoteNote?: unknown;
    aiGeneratedItems?: unknown;
    originalAiScope?: unknown;
    quoteData?: unknown;
    workMethodOverallProgramme?: unknown;
    projectStrategy?: unknown;
    projectWMS?: unknown;
    pricingMatchesDismissed?: unknown;
    appliedLearnings?: unknown;
    learningsRetrievalMode?: unknown;
    projectDefaultMargin?: unknown;
}

export interface ProjectHydrationFingerprintReceipt {
    projectId: string;
    loadGeneration: number;
    /** Exact live state observed immediately before the load-owned setter. */
    beforeFingerprint: string;
    /** Exact live state expected after that setter paints. */
    afterFingerprint: string;
    /** Durable baseline with only the load-owned lane rebased onto it. */
    rebasedBaselineFingerprint: string;
    overrides?: Record<string, unknown>;
}

interface ProjectHydrationContext {
    projectId: unknown;
    loadGeneration: unknown;
}

type ProjectHydrationFingerprintReceiptRef = {
    current: ProjectHydrationFingerprintReceipt | null;
};

/**
 * Exact receipt for one load-owned hydration write.
 *
 * The project id and load generation prevent a late callback from project A
 * suppressing project B. The expected future fingerprint prevents the receipt
 * from absorbing a builder edit that React happened to batch with the media
 * write. Unlike the legacy two-second load guard, this remains valid for slow
 * IndexedDB, sync-folder, or object-storage recovery.
 */
export function createProjectHydrationFingerprintReceipt({
    projectId,
    loadGeneration,
    beforeFingerprint,
    afterFingerprint,
    rebasedBaselineFingerprint,
    overrides,
}: ProjectHydrationContext & {
    beforeFingerprint: unknown;
    afterFingerprint: unknown;
    rebasedBaselineFingerprint: unknown;
    overrides?: Record<string, unknown>;
}): ProjectHydrationFingerprintReceipt | null {
    const normalizedProjectId = String(projectId ?? '').trim();
    const normalizedLoadGeneration = Number(loadGeneration);
    if (!normalizedProjectId
        || !Number.isFinite(normalizedLoadGeneration)
        || typeof beforeFingerprint !== 'string'
        || beforeFingerprint.length === 0
        || typeof afterFingerprint !== 'string'
        || afterFingerprint.length === 0
        || typeof rebasedBaselineFingerprint !== 'string'
        || rebasedBaselineFingerprint.length === 0) return null;
    return {
        projectId: normalizedProjectId,
        loadGeneration: normalizedLoadGeneration,
        beforeFingerprint,
        afterFingerprint,
        rebasedBaselineFingerprint,
        ...(overrides ? { overrides } : {}),
    };
}

/**
 * Resolve a hydration receipt without confusing a scheduled pre-paint effect
 * for a user edit:
 *
 * - exact before state: retain the receipt for the subsequent media paint;
 * - exact after state: consume and return the baseline with only that media
 *   lane rebased;
 * - anything else (including another project/load): clear it and let the
 *   normal dirty comparator run.
 */
export function consumeProjectHydrationFingerprintReceipt(
    receiptRef: ProjectHydrationFingerprintReceiptRef,
    context: ProjectHydrationContext,
    currentFingerprint: string,
): string | null {
    const receipt = receiptRef.current;
    if (!receipt) return null;
    if (receipt.projectId !== String(context.projectId ?? '').trim()
        || receipt.loadGeneration !== Number(context.loadGeneration)) {
        receiptRef.current = null;
        return null;
    }
    // If normalisation makes the before/after fingerprints identical, the
    // load-owned transition is semantically a no-op and can be consumed now.
    if (receipt.afterFingerprint === currentFingerprint
        && receipt.beforeFingerprint === receipt.afterFingerprint) {
        receiptRef.current = null;
        return receipt.rebasedBaselineFingerprint;
    }
    if (receipt.beforeFingerprint === currentFingerprint) return null;
    receiptRef.current = null;
    if (receipt.afterFingerprint !== currentFingerprint) return null;
    return receipt.rebasedBaselineFingerprint;
}

/**
 * Project saves may persist an explicit future state before React has painted
 * that state (for example Stage 4 -> 5 navigation, photo-link writeback, or a
 * Work Method hand-off). Overlay those save-envelope aliases onto the current
 * fingerprint input so a successful save establishes the baseline it actually
 * persisted, rather than the previous render. Operational-only save options
 * such as `silent` and `completedStages` are intentionally ignored.
 */
export function overlayProjectFingerprintSaveOverrides(
    current: ProjectFingerprintInput,
    overrides: Record<string, unknown> = {},
): ProjectFingerprintInput {
    const next: ProjectFingerprintInput = { ...current };
    const assign = (target: keyof ProjectFingerprintInput, source: string) => {
        if (overrides[source] !== undefined) next[target] = overrides[source];
    };

    assign('quoteTitle', 'quoteTitle');
    assign('titleEditedByUser', 'titleEditedByUser');
    assign('formData', 'formData');
    assign('stage', 'currentStage');
    assign('projectWorkflowMode', 'workflowMode');
    assign('legacyBannerDismissed', 'legacyBannerDismissed');
    assign('photos', 'photos');
    assign('documents', 'documents');
    assign('videos', 'videos');
    assign('videoTranscripts', 'videoTranscripts');
    assign('selectedHeroImage', 'selectedHeroImage');
    assign('projectSummary', 'projectSummary');
    assign('analysis', 'riskAnalysis');
    assign('photoAnalysis', 'photoAnalysis');
    assign('scopeGstInclusive', 'scopeGstInclusive');
    assign('scope', 'scope');
    assign('quoteNote', 'quoteNote');
    assign('originalAiScope', 'originalAiScope');
    assign('quoteData', 'quoteData');
    assign('workMethodOverallProgramme', 'workMethodOverallProgramme');
    assign('projectStrategy', 'projectStrategy');
    assign('projectWMS', 'wms');
    assign('pricingMatchesDismissed', 'pricingMatchesDismissed');
    assign('appliedLearnings', 'appliedLearnings');
    assign('learningsRetrievalMode', 'learningsRetrievalMode');
    assign('projectDefaultMargin', 'defaultMarginPercent');
    return next;
}

/**
 * Rebase only the lanes that media portability changed onto the exact
 * save-start fingerprint. Reading live React state again after async uploads
 * could absorb an operator edit that was never part of the persisted write.
 */
export function rebaseProjectFingerprintWithSaveOverrides(
    baseFingerprint: string | null,
    overrides: Record<string, unknown> = {},
): string | null {
    if (baseFingerprint === null) return null;
    try {
        const parsed = JSON.parse(baseFingerprint);
        if (!parsed || typeof parsed !== 'object' || Array.isArray(parsed)) return baseFingerprint;
        const patch = overlayProjectFingerprintSaveOverrides({} as ProjectFingerprintInput, overrides);
        return stableStringify({ ...parsed, ...patch });
    } catch {
        // False-dirty is safer than absorbing a concurrent edit into a saved
        // baseline when an older/unexpected fingerprint is not parseable.
        return baseFingerprint;
    }
}

/**
 * An explicit Stage 5 Save first establishes a baseline from the canonical
 * server receipt. The editor then binds that receipt to its deterministic
 * presentation shape and republishes exactly three App-owned lanes. Rebase
 * only those lanes so the presentation paint cannot become a nav-away save,
 * while a concurrent builder edit anywhere else remains dirty.
 */
export function rebaseProjectFingerprintForAdoptedStage5Presentation(
    baseFingerprint: string | null,
    adoptedPresentation: {
        quoteData?: unknown;
        scope?: unknown;
        selectedHeroImage?: unknown;
    },
): string | null {
    return rebaseProjectFingerprintWithSaveOverrides(baseFingerprint, {
        quoteData: adoptedPresentation.quoteData,
        scope: adoptedPresentation.scope,
        selectedHeroImage: adoptedPresentation.selectedHeroImage,
    });
}

/**
 * Build the comparable fingerprint of a project's persisted state.
 *
 * Returns a string; compare two with `===`. The value is opaque — do not parse
 * it, persist it, or send it to the server.
 */
/**
 * Columns of a SERVER project row that can move without the operator having
 * changed anything, and therefore must not read as a content edit.
 *
 * Both spellings are listed because a row reaches the client as snake_case from
 * the API and camelCase after `apiFetch` conversion, and a Dexie-shaped record
 * can carry either (see the apiFetch camelCase HARD RULE in CLAUDE.md).
 *
 * - `updated_at` is the optimistic-concurrency token itself. Comparing it would
 *   make every comparison differ by construction.
 * - `ai_cost_cents` is rolled up from the AI ledger by a database trigger
 *   (migration 041/096). It is derived, not typed, and cannot be lost.
 * - `qm_quote_no` is minted by the server and preserved by every subsequent
 *   save via COALESCE, so a concurrent allocation cannot cost the operator
 *   anything either.
 *
 * - `cachedServerRowUpdatedAt` and `lifecycleReceiptsAt` are this device's
 *   cache bookkeeping for the offline project summary
 *   (services/project-summary-job-fields.ts). No server row has them, so a
 *   cached row that carries them (the batch-hydrated row a load paints) would
 *   otherwise never match a fresh read of the same server row.
 *
 * Everything else is INCLUDED, including columns added in future. That is the
 * load-bearing direction: an unknown column that moved reads as a real content
 * change and puts the decision in front of the operator. The opposite default
 * would let a genuine cross-device edit be auto-overwritten, which is the data
 * loss this whole mechanism exists to prevent.
 */
const SERVER_VOLATILE_KEYS = new Set([
    'updated_at', 'updatedAt',
    'ai_cost_cents', 'aiCostCents',
    'qm_quote_no', 'qmQuoteNo',
    'cachedServerRowUpdatedAt', 'lifecycleReceiptsAt',
]);

/**
 * A comparable signature of the CONTENT of a project row as the server holds it.
 *
 * Only ever compare two of these against each other — the same row as this tab
 * last saw it, versus that row now. It is NOT comparable with
 * `computeProjectFingerprint`, which describes local React state under
 * different field names.
 *
 * The question it answers is narrow and specific: when a save is refused with
 * `PROJECT_STALE_WRITE`, did somebody actually change this project, or did only
 * bookkeeping move? A false "changed" costs the operator a banner they did not
 * need. A false "unchanged" would silently overwrite somebody's work, so every
 * ambiguity — an unreadable row, an unexpected shape, a new column — resolves
 * to "changed".
 */
export function serverProjectContentSignature(row: unknown): string {
    if (!row || typeof row !== 'object') return `[unreadable-row:${Math.random()}]`;
    const content: Record<string, unknown> = {};
    for (const [key, value] of Object.entries(row as Record<string, unknown>)) {
        if (SERVER_VOLATILE_KEYS.has(key)) continue;
        content[key] = value;
    }
    return stableStringify(content);
}

/**
 * 2026-08-19 (Peter: "it auto saved when I didn't make any changes in
 * Stage 4"): name WHICH top-level slices differ between two fingerprints so
 * every dirty flip and autosave can attribute itself in the console instead
 * of being a mystery. Both inputs are computeProjectFingerprint outputs
 * (stableStringify of a flat object), so parsing and re-serialising each key
 * gives an exact per-slice comparison. Runs only on the dirty FLIP, never
 * per keystroke.
 */
export function diffProjectFingerprintKeys(prevFingerprint: string | null, nextFingerprint: string | null): string[] {
    if (!prevFingerprint || !nextFingerprint || prevFingerprint === nextFingerprint) return [];
    let prev: Record<string, unknown>;
    let next: Record<string, unknown>;
    try {
        prev = JSON.parse(prevFingerprint);
        next = JSON.parse(nextFingerprint);
    } catch {
        return ['(fingerprints not comparable)'];
    }
    const keys = new Set([...Object.keys(prev ?? {}), ...Object.keys(next ?? {})]);
    const changed: string[] = [];
    for (const key of keys) {
        if (stableStringify(prev?.[key] ?? null) !== stableStringify(next?.[key] ?? null)) changed.push(key);
    }
    return changed.sort();
}

export function computeProjectFingerprint(input: ProjectFingerprintInput): string {
    // The visible stage is navigation state, not authored Project content.
    // Pipeline/generation seams persist their completed stage explicitly.
    // Back, Forward and stage-strip clicks must never arm the full-project
    // media autosave merely because the operator inspected another view.
    return stableStringify({
        quoteTitle: input.quoteTitle ?? '',
        titleEditedByUser: input.titleEditedByUser ?? false,
        formData: input.formData ?? {},
        projectWorkflowMode: input.projectWorkflowMode ?? null,
        legacyBannerDismissed: input.legacyBannerDismissed ?? false,
        photos: input.photos ?? [],
        documents: input.documents ?? [],
        videos: input.videos ?? [],
        videoTranscripts: input.videoTranscripts ?? null,
        selectedHeroImage: input.selectedHeroImage ?? null,
        projectSummary: input.projectSummary ?? null,
        analysis: input.analysis ?? null,
        photoAnalysis: input.photoAnalysis ?? null,
        scopeGstInclusive: input.scopeGstInclusive ?? false,
        scope: input.scope ?? [],
        quoteNote: input.quoteNote ?? '',
        aiGeneratedItems: input.aiGeneratedItems ?? null,
        originalAiScope: input.originalAiScope ?? null,
        quoteData: input.quoteData ?? null,
        workMethodOverallProgramme: input.workMethodOverallProgramme ?? null,
        projectStrategy: input.projectStrategy ?? null,
        projectWMS: input.projectWMS ?? null,
        pricingMatchesDismissed: input.pricingMatchesDismissed ?? null,
        appliedLearnings: input.appliedLearnings ?? null,
        learningsRetrievalMode: input.learningsRetrievalMode ?? null,
        projectDefaultMargin: input.projectDefaultMargin ?? null,
    });
}
